import type { AccessRole, NavLink } from '@/types'

export const navLinks: NavLink[] = [
    { label: "Simulations", href: '#simulations' },
    { label: "The lab", href: '#lab' },
    { label: "How it works", href: '#how-it-works' },
    { label: "Pricing", href: '#pricing' },
    { label: "FAQ", href: '#faq' }
]

export const heroHighlights: string[] = [
    "Free for students",
    "Runs in any browser",
    "Mapped to AP, IB and A-level"
]

export const topics: string[] = [
    "Classical mechanics",
    "Gravitation",
    "Waves and optics",
    "Electromagnetism",
    "Thermodynamics",
    "Quantum mechanics",
    "Special relativity",
    "Fluid dynamics",
    "Nuclear physics",
    "Astrophysics"
]

export type SimulationKey = 'pendulum' | 'orbit' | 'interference' | 'dipole' | 'gas' | 'orbital'

export type SimulationCard = {
    key: SimulationKey
    figure: string
    field: string
    count: number
    title: string
    description: string
    caption: string
}

export const simulationCards: SimulationCard[] = [
    {
        key: 'pendulum',
        figure: "Fig. 1.1",
        field: "Classical mechanics",
        count: 28,
        title: "Double pendulum",
        description: "Two rods, one hinge, endless outcomes. Watch deterministic chaos unfold from a nudge smaller than a millimetre.",
        caption: "Animated double pendulum tracing a chaotic path"
    },
    {
        key: 'orbit',
        figure: "Fig. 1.2",
        field: "Gravitation",
        count: 19,
        title: "Kepler orbits",
        description: "A planet sweeps equal areas in equal times. The shaded wedge stays the same size whether it is racing or crawling.",
        caption: "Planet on an elliptical orbit with its swept area shaded"
    },
    {
        key: 'interference',
        figure: "Fig. 1.3",
        field: "Waves and optics",
        count: 24,
        title: "Two-source interference",
        description: "Crests meet crests, troughs cancel crests. As the sources drift apart, the fringes fan out and rearrange.",
        caption: "Ripples from two sources forming an interference pattern"
    },
    {
        key: 'dipole',
        figure: "Fig. 1.4",
        field: "Electromagnetism",
        count: 21,
        title: "Electric dipole",
        description: "Field lines leave the positive charge and land on the negative one. Move them closer and every line bends to follow.",
        caption: "Electric field lines flowing between a positive and a negative charge"
    },
    {
        key: 'gas',
        figure: "Fig. 1.5",
        field: "Thermodynamics",
        count: 17,
        title: "Ideal gas",
        description: "Seventy particles start at the same speed. Within seconds their speeds spread into the Maxwell-Boltzmann curve.",
        caption: "Gas particles colliding with a live histogram of their speeds"
    },
    {
        key: 'orbital',
        figure: "Fig. 1.6",
        field: "Quantum mechanics",
        count: 31,
        title: "Hydrogen 3d orbital",
        description: "Two thousand samples of a real wavefunction, colored by phase. The shape of an electron, finally visible.",
        caption: "Rotating probability cloud of a hydrogen 3d orbital"
    }
]

export type Step = {
    number: string
    title: string
    description: string
}

export const steps: Step[] = [
    {
        number: "01",
        title: "Pick a phenomenon",
        description: "Browse more than 140 simulations organised by topic and syllabus, from projectile motion to quantum tunnelling."
    },
    {
        number: "02",
        title: "Pull the levers",
        description: "Every constant is a slider and every object is draggable. Change mass, charge, gravity or the flow of time itself."
    },
    {
        number: "03",
        title: "Read the math",
        description: "Graphs, vectors and equations update live and share colors with the objects they describe, so every symbol has a body."
    }
]

export type Stat = {
    value: number
    suffix: string
    label: string
}

export const stats: Stat[] = [
    { value: 140, suffix: "+", label: "live simulations" },
    { value: 18, suffix: "", label: "topics, school to university" },
    { value: 240, suffix: " Hz", label: "fixed physics timestep" },
    { value: 0, suffix: "", label: "installs or plugins" }
]

export type Testimonial = {
    quote: string
    name: string
    role: string
    initials: string
}

export const testimonials: Testimonial[] = [
    {
        quote: "My students used to memorise the pendulum formula. Now they argue about why the period ignores mass, and that argument is the whole point of physics.",
        name: "Helena Ortiz",
        role: "Physics teacher, early-access school",
        initials: "HO"
    },
    {
        quote: "Interference finally clicked when I dragged the second source and watched the fringes bend. Two years of textbook diagrams never did that.",
        name: "Kwame Mensah",
        role: "First-year engineering student",
        initials: "KM"
    },
    {
        quote: "The integrators are genuinely correct. I run the orbit simulations live in my mechanics lectures without having to apologise for them.",
        name: "Anika Sørensen",
        role: "Lecturer in computational physics",
        initials: "AS"
    }
]

export type Plan = {
    name: string
    description: string
    monthly: number | null
    yearly: number | null
    priceNote: string
    features: string[]
    cta: string
    role: AccessRole | null
    featured: boolean
}

export const plans: Plan[] = [
    {
        name: "Student",
        description: "Everything you need to learn physics on your own terms.",
        monthly: 0,
        yearly: 0,
        priceNote: "Free forever",
        features: [
            "All 140+ live simulations",
            "Live equations, graphs and vectors",
            "Save up to 10 experiments",
            "Works on phones, tablets and laptops"
        ],
        cta: "Start free",
        role: 'student',
        featured: false
    },
    {
        name: "Pro",
        description: "For serious learners, exam prep and independent tutors.",
        monthly: 8,
        yearly: 6,
        priceNote: "per month",
        features: [
            "Everything in Student",
            "Unlimited saved experiments",
            "Export graphs and data as CSV",
            "Guided step-by-step derivations",
            "First access to new simulations"
        ],
        cta: "Get Pro early",
        role: null,
        featured: true
    },
    {
        name: "Schools",
        description: "For classrooms, departments and whole districts.",
        monthly: null,
        yearly: null,
        priceNote: "Tailored to your school",
        features: [
            "Teacher dashboard and assignments",
            "Google Classroom and LMS sync",
            "Class-wide understanding analytics",
            "Single sign-on and admin controls",
            "Onboarding for your department"
        ],
        cta: "Talk to us",
        role: 'school',
        featured: false
    }
]

export type Faq = {
    question: string
    answer: string
}

export const faqs: Faq[] = [
    {
        question: "Do I need to install anything?",
        answer: "No. Inertia runs entirely in your browser on phones, tablets, laptops and school Chromebooks. If a device can open a website, it can run the lab."
    },
    {
        question: "How accurate are the simulations?",
        answer: "Each one is solved numerically with the methods physicists use, such as symplectic and Runge-Kutta integrators at a fixed 240 Hz timestep. When a model simplifies reality, for example by ignoring air resistance, the simulation tells you so."
    },
    {
        question: "Which syllabi do you cover?",
        answer: "Simulations are tagged for AP Physics 1, 2 and C, IB Physics, A-level and introductory university courses, so you can filter by exactly what you are studying."
    },
    {
        question: "Is it really free for students?",
        answer: "Yes. The Student plan includes every simulation with no time limit and no ads. Pro adds unlimited saved experiments, data export and guided derivations."
    },
    {
        question: "Can teachers use it with a whole class?",
        answer: "Absolutely. The Schools plan adds assignments, a teacher dashboard and class analytics. Choose School when you join the list and we will set up a pilot with you."
    },
    {
        question: "When does early access open?",
        answer: "We are letting people in a few hundred at a time. Join the list and we will reach out as soon as your spot is ready."
    }
]

export const accessRoles: { value: AccessRole, label: string }[] = [
    { value: 'student', label: "Student" },
    { value: 'educator', label: "Educator" },
    { value: 'school', label: "School" }
]

export const footerColumns: { title: string, links: NavLink[] }[] = [
    {
        title: "Product",
        links: [
            { label: "Simulations", href: '#simulations' },
            { label: "The lab", href: '#lab' },
            { label: "Pricing", href: '#pricing' }
        ]
    },
    {
        title: "Learn",
        links: [
            { label: "How it works", href: '#how-it-works' },
            { label: "Platform", href: '#platform' },
            { label: "FAQ", href: '#faq' }
        ]
    },
    {
        title: "Get started",
        links: [
            { label: "Early access", href: '#access' },
            { label: "For schools", href: '#pricing' }
        ]
    }
]
